// stores/settings.js - 앱 설정 상태 관리
import { writable, get } from 'svelte/store';

const STORAGE_KEYS = {
  scanMode: 'scantodesk_scan_mode',
  paperSize: 'scantodesk_paper_size',
  defaultFilter: 'scantodesk_default_filter',
  executionProvider: 'scantodesk_execution_provider',
  locale: 'scantodesk_locale',
};

/**
 * @typedef {'simple' | 'document'} ScanMode
 * @typedef {'a4' | 'letter'} PaperSize
 * @typedef {'bw' | 'gray' | 'color'} FilterType
 * @typedef {'auto' | 'wasm'} ExecutionProvider
 */

/** 스캔 모드 */
export const scanMode = writable('simple');

/** 용지 크기 */
export const paperSize = writable('a4');

/** 기본 필터 */
export const defaultFilter = writable('gray');

/** ML 실행 제공자 */
export const executionProvider = writable('auto');

/** 언어 */
export const locale = writable('ko');

/**
 * 설정 초기화 (localStorage에서 복원)
 */
export function initSettings() {
  // 스캔 모드
  const storedMode = localStorage.getItem(STORAGE_KEYS.scanMode);
  if (storedMode === 'simple' || storedMode === 'document') {
    scanMode.set(storedMode);
  }

  // 용지 크기
  const storedPaper = localStorage.getItem(STORAGE_KEYS.paperSize);
  if (storedPaper === 'a4' || storedPaper === 'letter') {
    paperSize.set(storedPaper);
  }

  // 기본 필터
  const storedFilter = localStorage.getItem(STORAGE_KEYS.defaultFilter);
  if (storedFilter === 'bw' || storedFilter === 'gray' || storedFilter === 'color') {
    defaultFilter.set(storedFilter);
  }

  // 실행 제공자
  const storedProvider = localStorage.getItem(STORAGE_KEYS.executionProvider);
  if (storedProvider === 'auto' || storedProvider === 'wasm') {
    executionProvider.set(storedProvider);
  }

  // 언어
  const storedLocale = localStorage.getItem(STORAGE_KEYS.locale);
  if (storedLocale) {
    locale.set(storedLocale);
  } else {
    // 브라우저 언어 감지
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('ko')) {
      locale.set('ko');
    } else {
      locale.set('en');
    }
  }

  // 변경 시 localStorage에 저장
  scanMode.subscribe(v => {
    if (v) localStorage.setItem(STORAGE_KEYS.scanMode, v);
  });

  paperSize.subscribe(v => {
    if (v) localStorage.setItem(STORAGE_KEYS.paperSize, v);
  });

  defaultFilter.subscribe(v => {
    if (v) localStorage.setItem(STORAGE_KEYS.defaultFilter, v);
  });

  executionProvider.subscribe(v => {
    if (v) localStorage.setItem(STORAGE_KEYS.executionProvider, v);
  });

  locale.subscribe(v => {
    if (v) localStorage.setItem(STORAGE_KEYS.locale, v);
  });
}

// --- Getters for external use ---

export function getScanMode() {
  return get(scanMode);
}

export function getPaperSize() {
  return get(paperSize);
}

export function getDefaultFilter() {
  return get(defaultFilter);
}

export function getExecutionProvider() {
  return get(executionProvider);
}

export function getLocale() {
  return get(locale);
}

// --- Paper size utilities ---

const PAPER_SIZES = {
  a4: { width: 210, height: 297, ratio: 210 / 297 },
  letter: { width: 216, height: 279, ratio: 216 / 279 },
};

/**
 * 현재 용지 크기의 가로세로 비율
 * @returns {number}
 */
export function getPaperAspectRatio() {
  const size = get(paperSize);
  return PAPER_SIZES[size]?.ratio || PAPER_SIZES.a4.ratio;
}

/**
 * 현재 용지 크기 정보
 * @returns {{ width: number, height: number, ratio: number }}
 */
export function getPaperSizeInfo() {
  const size = get(paperSize);
  return PAPER_SIZES[size] || PAPER_SIZES.a4;
}

/**
 * 원근 보정 출력 크기 계산
 * 용지 비율에 맞춰 적절한 해상도로 출력
 * @param {number} targetWidth - 목표 너비 (기본 1200px)
 * @returns {{ width: number, height: number }}
 */
export function getOutputSize(targetWidth = 1200) {
  const ratio = getPaperAspectRatio();
  return {
    width: targetWidth,
    height: Math.round(targetWidth / ratio)
  };
}
